import React, { Component } from 'react';
import { isEmpty, isEqual, omit, omitBy } from 'lodash';

// Todo: type this better
type FormState = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [id: string]: any;
};

// Todo: type this better
export interface IResetableFormRenderProps<T extends FormState = FormState> {
  errorState: FormState; // id => error mapping
  formState: T; // merged values (initial + edited) on a per id basis
  hasError: boolean; // !isEmpty(errorState)
  isEdited: boolean; // !isEmpty(editedState)
  onReset: () => void; // clears out editedFormState so formState equals initialFormState
  onSubmit: () => void; // called w/ merged form state
  onFormSubmit: (e: React.FormEvent) => void; // hack to prevent form submission
  onUpdate: <K extends keyof T>(id: K, value: T[K]) => void; // editedFormState[id] = value if !== initial value
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onUpdateMany: (values: Array<{ id: keyof T; value: any }>) => void; // same as onUpdate, takes in an array of fields to update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setError: (id: keyof T, value: any) => void; // errorFormState[id] = value if !== initial value
}

interface IResetableFormState<T extends FormState = FormState> {
  editedFormState: T;
  errorFormState: FormState;
}

export interface IResetableFormProps<T extends FormState = FormState> {
  initialFormState: T; // initial baseline form state
  onSubmit: (submitPayload: { formState: T }) => void;
  render: (renderProps: IResetableFormRenderProps<T>) => React.ReactNode;
}

/**
 * A generic resetable form components that handles render / update state of a form.
 *
 * component-test-check-ignore
 * storybook-check-ignore
 */
export class ResetableForm<T extends FormState = FormState> extends Component<
  IResetableFormProps<T>,
  IResetableFormState<T>
> {
  public state: IResetableFormState<T> = {
    editedFormState: {} as T,
    errorFormState: {},
  };

  /** TODO: Add documentation */
  public UNSAFE_componentWillReceiveProps(nextProps: IResetableFormProps<T>): void {
    const newInitialFormState = nextProps.initialFormState;

    if (newInitialFormState !== this.props.initialFormState) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const newEditedFormState = omitBy(this.state.editedFormState, (val: any, key: string) =>
        isEqual(val, newInitialFormState[key]),
      ) as T;

      this.setState({ editedFormState: newEditedFormState });
    }
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */
  /** TODO: Add documentation */
  public setError: IResetableFormRenderProps<T>['setError'] = (id, error) => {
    this.setState((prevState) => ({
      errorFormState: error
        ? { ...prevState.errorFormState, [id]: error }
        : omit(prevState.errorFormState, id),
    }));
  };

  /** TODO: Add documentation */
  public onUpdate: IResetableFormRenderProps<T>['onUpdate'] = (id, value) => {
    const { initialFormState } = this.props;
    const { editedFormState } = this.state;
    const prevValue = initialFormState[id];

    // rough check: if updated value is set back to equal initial, it's not considered an edit
    // we CAN use a custom comparator, most likely don't need it
    if (isEqual(prevValue, value)) {
      this.setState({ editedFormState: omit(editedFormState, id) as T });
    } else {
      this.setState({
        editedFormState: {
          ...editedFormState,
          [id]: value,
        },
      });
    }
  };

  /* same as onUpdate, but updates multiple fields in one render */
  public onUpdateMany: IResetableFormRenderProps<T>['onUpdateMany'] = (newValues) => {
    const { editedFormState } = this.state;
    const newFieldValues: Partial<T> = {};

    newValues?.forEach((v) => {
      newFieldValues[v.id] = v.value;
    });

    this.setState({
      editedFormState: {
        ...editedFormState,
        ...newFieldValues,
      },
    });
  };

  /** TODO: Add documentation */
  public onFormSubmit: IResetableFormRenderProps<T>['onFormSubmit'] = (e: React.FormEvent) => {
    e.preventDefault();
    this.onSubmit();
  };

  /** TODO: Add documentation */
  public onSubmit: IResetableFormRenderProps<T>['onSubmit'] = () => {
    const { initialFormState } = this.props;
    const { editedFormState } = this.state;

    this.props.onSubmit({
      formState: {
        ...initialFormState,
        ...editedFormState,
      },
    });
  };

  /** TODO: Add documentation */
  public onReset: IResetableFormRenderProps<T>['onReset'] = () => {
    this.setState({
      editedFormState: {} as T,
      errorFormState: {},
    });
  };

  /** TODO: Add documentation */
  public render(): React.ReactNode {
    const { initialFormState } = this.props;
    const { errorFormState, editedFormState } = this.state;

    return this.props.render({
      errorState: errorFormState,
      formState: {
        ...initialFormState,
        ...editedFormState,
      },
      hasError: !isEmpty(errorFormState),
      isEdited: !isEmpty(editedFormState),
      onFormSubmit: this.onFormSubmit,
      onReset: this.onReset,
      onSubmit: this.onSubmit,
      onUpdate: this.onUpdate,
      onUpdateMany: this.onUpdateMany,
      setError: this.setError,
    });
  }
}
